import { useCallback, useRef, useState } from "react";
import {
  Map,
  AdvancedMarker,
  useMapsLibrary,
  type MapMouseEvent,
} from "@vis.gl/react-google-maps";
import { toast } from "sonner";
import {
  GoogleMapsProvider,
  hasGoogleMapsKey,
} from "@/components/ui/GoogleMapsProvider";
import { useLanguage } from "@/contexts/LanguageContext";
import { getComponent, type PlaceAddressData } from "@/components/ui/PlacesAutocomplete";

const RIYADH = { lat: 24.7136, lng: 46.6753 };

interface MapPinPickerProps {
  latitude: number | null;
  longitude: number | null;
  onLocationChange: (lat: number, lng: number) => void;
  onPlaceSelect?: (data: PlaceAddressData) => void;
}

function MapPinPickerInner({
  latitude,
  longitude,
  onLocationChange,
  onPlaceSelect,
}: MapPinPickerProps) {
  const { t } = useLanguage();
  const geocoding = useMapsLibrary("geocoding");
  const geocoderRef = useRef<google.maps.Geocoder | null>(null);
  const [resolvedAddress, setResolvedAddress] = useState<string | null>(null);

  const hasPosition = latitude != null && longitude != null;
  const position = hasPosition ? { lat: latitude, lng: longitude } : null;

  const reverseGeocode = useCallback(
    (lat: number, lng: number) => {
      if (!geocoding) return;
      if (!geocoderRef.current) {
        geocoderRef.current = new geocoding.Geocoder();
      }
      geocoderRef.current.geocode(
        { location: { lat, lng } },
        (results, status) => {
          if (status !== "OK" || !results?.[0]) {
            toast.error(t(
              "تعذر تحديد العنوان لهذا الموقع",
              "Could not determine address for this location"
            ));
            setResolvedAddress(null);
            return;
          }
          const place = results[0];
          const components = place.address_components || [];
          const formattedAddress = place.formatted_address || "";
          setResolvedAddress(formattedAddress);
          onPlaceSelect?.({
            region: getComponent(components, "administrative_area_level_1"),
            city:
              getComponent(components, "locality") ||
              getComponent(components, "administrative_area_level_2"),
            district:
              getComponent(components, "sublocality_level_1") ||
              getComponent(components, "sublocality") ||
              getComponent(components, "neighborhood"),
            streetName: getComponent(components, "route"),
            buildingNumber: getComponent(components, "street_number"),
            postalCode: getComponent(components, "postal_code"),
            latitude: lat,
            longitude: lng,
            formattedAddress,
          });
        }
      );
    },
    [geocoding, onPlaceSelect, t]
  );

  const handleMapClick = useCallback(
    (e: MapMouseEvent) => {
      const latLng = e.detail.latLng;
      if (latLng) {
        onLocationChange(latLng.lat, latLng.lng);
        reverseGeocode(latLng.lat, latLng.lng);
      }
    },
    [onLocationChange, reverseGeocode]
  );

  const handleDragEnd = useCallback(
    (e: google.maps.MapMouseEvent) => {
      const latLng = e.latLng;
      if (latLng) {
        const lat = latLng.lat();
        const lng = latLng.lng();
        onLocationChange(lat, lng);
        reverseGeocode(lat, lng);
      }
    },
    [onLocationChange, reverseGeocode]
  );

  return (
    <div className="space-y-1">
      <p className="text-xs text-muted-foreground px-1">
        {t("انقر على الخريطة لتحديد الموقع، أو اسحب المؤشر لتعديله", "Click on the map to set location, or drag the pin to adjust")}
      </p>
      <div className="h-48 rounded-lg overflow-hidden border cursor-crosshair">
        <Map
          defaultZoom={hasPosition ? 15 : 6}
          defaultCenter={position || RIYADH}
          gestureHandling="greedy"
          mapId="property-pin-picker"
          disableDefaultUI
          zoomControl
          onClick={handleMapClick}
        >
          {position && (
            <AdvancedMarker
              position={position}
              draggable
              onDragEnd={handleDragEnd}
            />
          )}
        </Map>
      </div>
      {resolvedAddress && (
        <p className="text-xs text-muted-foreground px-1">
          {resolvedAddress}
        </p>
      )}
    </div>
  );
}

export function MapPinPicker(props: MapPinPickerProps) {
  if (!hasGoogleMapsKey) return null;

  return (
    <GoogleMapsProvider>
      <MapPinPickerInner {...props} />
    </GoogleMapsProvider>
  );
}
